import React from 'react';
import { Box, Chip, Divider, Typography } from '@material-ui/core';
import { DocumentationProcess } from '../../../interfaces/Documentation';
import { useStyles } from '../Style';

interface ProcessDetailProps {
    process: DocumentationProcess;
}

const ProcessDetail: React.FC<ProcessDetailProps> = ({ process }) => {
    const classes = useStyles();

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                {process.processName}
            </Typography>
            <Chip label={process.module} color="primary" size="small" className={classes.moduleChip} />
            <Divider style={{ margin: '16px 0' }} />

            <Typography variant="h6" className={classes.sectionTitle}>
                1. Visão Geral do Processo
            </Typography>
            <Box className={classes.sectionContent}>
                <Typography variant="subtitle2" gutterBottom><strong>Objetivo:</strong></Typography>
                <Typography variant="body2" paragraph>{process.overview}</Typography>
                <Typography variant="subtitle2" gutterBottom><strong>Contexto de Negócio:</strong></Typography>
                <Typography variant="body2" paragraph>{process.businessContext}</Typography>
                <Typography variant="subtitle2" gutterBottom><strong>Áreas Envolvidas:</strong></Typography>
                <Typography variant="body2">{process.involvedAreas}</Typography>
            </Box>

            <Typography variant="h6" className={classes.sectionTitle}>
                2. Fluxo do Processo
            </Typography>
            <Box className={classes.sectionContent}>
                <Typography variant="subtitle2" gutterBottom><strong>Passo a Passo:</strong></Typography>
                <ul className={classes.stepList}>
                    {process.flow.steps.map((step, index) => (
                        <li key={index} className={classes.stepItem}>
                            <Typography variant="body2">{step}</Typography>
                        </li>
                    ))}
                </ul>
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom><strong>Entradas:</strong></Typography>
                    {process.flow.inputs.map((input, index) => (
                        <Chip key={index} label={input} size="small" variant="outlined" className={classes.chip} />
                    ))}
                </Box>
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom><strong>Saídas:</strong></Typography>
                    {process.flow.outputs.map((output, index) => (
                        <Chip key={index} label={output} size="small" variant="outlined" color="primary" className={classes.chip} />
                    ))}
                </Box>
            </Box>

            <Typography variant="h6" className={classes.sectionTitle}>
                3. Regras de Negócio
            </Typography>
            <Box className={classes.sectionContent}>
                <Typography variant="subtitle2" gutterBottom><strong>Validações:</strong></Typography>
                <ul>
                    {process.businessRules.validations.map((rule, index) => (
                        <li key={index}><Typography variant="body2">{rule}</Typography></li>
                    ))}
                </ul>
                <Typography variant="subtitle2" gutterBottom><strong>Condições Obrigatórias:</strong></Typography>
                <ul>
                    {process.businessRules.mandatoryConditions.map((condition, index) => (
                        <li key={index}><Typography variant="body2">{condition}</Typography></li>
                    ))}
                </ul>
                <Typography variant="subtitle2" gutterBottom><strong>Cenários de Exceção:</strong></Typography>
                <ul>
                    {process.businessRules.exceptionScenarios.map((scenario, index) => (
                        <li key={index}><Typography variant="body2">{scenario}</Typography></li>
                    ))}
                </ul>
            </Box>

            <Typography variant="h6" className={classes.sectionTitle}>
                4. Parâmetros
            </Typography>
            <Box className={classes.sectionContent}>
                <table className={classes.fieldTable}>
                    <thead>
                        <tr>
                            <th>Campo</th>
                            <th>Descrição</th>
                            <th>Valores Aceitos</th>
                            <th>Obrigatório</th>
                        </tr>
                    </thead>
                    <tbody>
                        {process.parameters.fields.map((field, index) => (
                            <tr key={index}>
                                <td>{field.fieldName}</td>
                                <td>{field.description}</td>
                                <td>{field.acceptedValues}</td>
                                <td>{field.required ? 'Sim' : 'Não'}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Box mt={2}>
                    <Typography variant="subtitle2" gutterBottom><strong>Dependências:</strong></Typography>
                    {process.parameters.dependencies.map((dep, index) => (
                        <Chip key={index} label={dep} size="small" variant="outlined" className={classes.chip} />
                    ))}
                </Box>
            </Box>

            {process.integrations && process.integrations.items && process.integrations.items.length > 0 && (
                <>
                    <Typography variant="h6" className={classes.sectionTitle}>
                        5. Integrações
                    </Typography>
                    <Box className={classes.sectionContent}>
                        {process.integrations.items.map((integration, index) => (
                            <Box key={index} className={classes.integrationCard}>
                                <Typography variant="subtitle2">
                                    <strong>{integration.systemName}</strong>
                                    <Chip label={integration.integrationType} size="small" color="secondary" className={classes.chip} />
                                </Typography>
                                <Typography variant="body2">{integration.description}</Typography>
                            </Box>
                        ))}
                    </Box>
                </>
            )}

            {process.plantVariation && process.plantVariation.variations && process.plantVariation.variations.length > 0 && (
                <>
                    <Typography variant="h6" className={classes.sectionTitle}>
                        6. Variação por Planta
                    </Typography>
                    <Box className={classes.sectionContent}>
                        {process.plantVariation.variations.map((variation, index) => (
                            <Box key={index} className={classes.plantSection}>
                                <Typography className={classes.plantName}>
                                    {variation.plantName}
                                </Typography>
                                <Typography variant="subtitle2" gutterBottom><strong>Regras Específicas:</strong></Typography>
                                <ul>
                                    {variation.specificRules.map((rule, ruleIndex) => (
                                        <li key={ruleIndex}><Typography variant="body2">{rule}</Typography></li>
                                    ))}
                                </ul>
                                <Typography variant="subtitle2" gutterBottom><strong>Diferenças de Comportamento:</strong></Typography>
                                <ul>
                                    {variation.behaviorDifferences.map((diff, diffIndex) => (
                                        <li key={diffIndex}><Typography variant="body2">{diff}</Typography></li>
                                    ))}
                                </ul>
                            </Box>
                        ))}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default ProcessDetail;
